import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faCircleXmark } from '@fortawesome/free-solid-svg-icons';

function IssueCard({ issue, variant }) {
  const isError = variant === 'error';
  return (
    <div
      style={{
        ...s.card,
        borderLeftColor: isError ? 'var(--error)' : 'var(--warning)',
      }}
    >
      <div style={s.cardHeader}>
        <span style={s.emoji} aria-hidden="true">
          {isError ? '🔴' : '🟡'}
        </span>
        <span style={s.cardTitle}>{issue.title}</span>
        {issue.severity && (
          <span style={s.severityTag}>{issue.severity}</span>
        )}
      </div>

      {issue.message && <div style={s.message}>{issue.message}</div>}

      {issue.location?.length > 0 && (
        <div style={s.subBlock}>
          <div style={s.subLabel}>Location</div>
          <div style={s.fieldGrid}>
            {issue.location.map(f => (
              <div key={f.label} style={s.fieldRow}>
                <span style={s.fieldLabel}>{f.label}</span>
                <span style={s.fieldValue}>{f.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {issue.fields?.length > 0 && (
        <div style={s.fieldGrid}>
          {issue.fields.map(f => (
            <div key={f.label} style={s.fieldRow}>
              <span style={s.fieldLabel}>{f.label}</span>
              <span style={s.fieldValue}>{f.value}</span>
            </div>
          ))}
        </div>
      )}

      {issue.fix && (
        <div style={s.fixRow}>
          <span style={s.fixLabel}>Recommendation</span>
          <span style={s.fixValue}>{issue.fix}</span>
        </div>
      )}

      {issue.example && (
        <div style={s.fixRow}>
          <span style={s.fixLabel}>Example Fix</span>
          <span style={s.fixValue}>{issue.example}</span>
        </div>
      )}
    </div>
  );
}

// Renders every error/warning the backend returned for a failed upload —
// no generic "file structure is not correct" fallback message.
export default function UploadValidationErrors({ issues, onChooseDifferentFile }) {
  const errors = issues?.errors || [];
  const warnings = issues?.warnings || [];

  return (
    <div style={s.page}>
      <div style={s.content}>
        {/* Header */}
        <div style={s.headerCard}>
          <div style={s.headerIconWrap}>
            <FontAwesomeIcon
              icon={faCircleXmark}
              style={s.headerIcon}
              aria-hidden="true"
            />
          </div>
          <div>
            <h1 style={s.headerTitle}>Dataset validation failed</h1>
            <p style={s.headerSub}>
              The uploaded file contains the following issues.
            </p>
          </div>
        </div>

        {/* Errors */}
        {errors.length > 0 && (
          <div style={s.block}>
            <div style={s.blockTitle}>
              Errors
              <span style={s.countBadge}>{errors.length}</span>
            </div>
            <div style={s.cardStack}>
              {errors.map((issue, i) => (
                <IssueCard key={i} issue={issue} variant="error" />
              ))}
            </div>
          </div>
        )}

        {/* Warnings */}
        {warnings.length > 0 && (
          <div style={s.block}>
            <div style={s.blockTitle}>
              Warnings
              <span style={{ ...s.countBadge, ...s.countBadgeWarn }}>
                {warnings.length}
              </span>
            </div>
            <div style={s.cardStack}>
              {warnings.map((issue, i) => (
                <IssueCard key={i} issue={issue} variant="warning" />
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <button
          type="button"
          onClick={onChooseDifferentFile}
          style={s.actionBtn}
        >
          <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
          Choose a different file
        </button>
      </div>
    </div>
  );
}

const s = {
  page: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    width: '100%',
    padding: '24px 20px 48px',
    boxSizing: 'border-box',
    background: 'var(--bg, #f8f7f4)',
    animation: 'fadeIn 0.3s ease',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 28,
    width: '100%',
    maxWidth: 640,
    margin: '0 auto',
  },
  headerCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 16,
    padding: '20px 22px',
    borderRadius: 16,
    background: 'rgba(255,255,255,0.7)',
    border: '1px solid rgba(0,0,0,0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  headerIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: 'rgba(239, 68, 68, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  headerIcon: {
    color: 'var(--error)',
    fontSize: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: 'var(--text-primary)',
    margin: '0 0 4px',
    letterSpacing: '-0.02em',
    lineHeight: 1.3,
  },
  headerSub: {
    fontSize: 14.5,
    color: 'var(--text-muted)',
    lineHeight: 1.5,
    margin: 0,
  },
  block: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  blockTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontSize: 12.5,
    fontWeight: 700,
    color: 'var(--text-primary)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  countBadge: {
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
    background: 'var(--error)',
    padding: '2px 8px',
    borderRadius: 999,
    letterSpacing: 0,
  },
  countBadgeWarn: {
    background: 'var(--warning)',
    color: '#1a1a1a',
  },
  cardStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  card: {
    borderLeft: '4px solid',
    borderRadius: 14,
    background: 'rgba(255,255,255,0.72)',
    border: '1px solid rgba(0,0,0,0.05)',
    padding: '16px 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  emoji: {
    fontSize: 14,
    lineHeight: 1,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: 'var(--text-primary)',
    flex: 1,
    lineHeight: 1.35,
  },
  severityTag: {
    fontSize: 10.5,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: 'var(--text-muted)',
    background: 'rgba(0,0,0,0.05)',
    padding: '3px 9px',
    borderRadius: 999,
    flexShrink: 0,
  },
  message: {
    fontSize: 13.5,
    color: 'var(--text-secondary)',
    lineHeight: 1.55,
  },
  subBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  subLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
  },
  fieldGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
  },
  fieldRow: {
    display: 'flex',
    gap: 12,
    fontSize: 13.5,
  },
  fieldLabel: {
    color: 'var(--text-muted)',
    minWidth: 100,
    fontWeight: 600,
    flexShrink: 0,
  },
  fieldValue: {
    color: 'var(--text-secondary)',
    wordBreak: 'break-word',
  },
  fixRow: {
    display: 'flex',
    gap: 12,
    fontSize: 13.5,
    paddingTop: 10,
    borderTop: '1px dashed var(--border-subtle)',
  },
  fixLabel: {
    color: 'var(--text-muted)',
    minWidth: 100,
    fontWeight: 600,
    flexShrink: 0,
  },
  fixValue: {
    color: 'var(--text-secondary)',
    wordBreak: 'break-word',
  },
  actionBtn: {
    marginTop: 8,
    alignSelf: 'flex-start',
    padding: '13px 20px',
    borderRadius: 12,
    border: '1px solid var(--border-subtle)',
    background: 'rgba(255,255,255,0.7)',
    color: 'var(--text-secondary)',
    fontSize: 14.5,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    fontFamily: 'var(--font-main)',
    transition: 'background 0.15s ease, border-color 0.15s ease',
  },
};